import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';

const AdminUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAdminUserById(userId);
      setUser(data);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading user details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <i className="fas fa-exclamation-triangle" style={styles.errorIcon}></i>
        <p style={styles.errorText}>{error}</p>
        <button style={styles.retryButton} onClick={fetchUserDetails}>
          Retry
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.errorContainer}>
        <i className="fas fa-user-slash" style={styles.errorIcon}></i>
        <p style={styles.errorText}>User not found</p>
        <button style={styles.retryButton} onClick={() => navigate('/admin/users')}>
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <button style={styles.backButton} onClick={() => navigate('/admin/users')}>
              <i className="fas fa-arrow-left"></i>
            </button>
            <div>
              <h1 style={styles.title}>User Details</h1>
              <p style={styles.subtitle}>{user.email}</p>
            </div>
          </div>
          <button style={styles.editButton} onClick={() => navigate(`/admin/users/${userId}/edit`)}>
            <i className="fas fa-edit" style={styles.buttonIcon}></i>
            Edit User
          </button>
        </div>
      </div>

      <div style={styles.profileCard}>
        <div style={styles.profileHeader}>
          <div style={styles.avatar}>
            <i className="fas fa-user" style={styles.avatarIcon}></i>
          </div>
          <div style={styles.profileInfo}>
            <h2 style={styles.profileName}>{user.username || user.email}</h2>
            <p style={styles.profileEmail}>{user.email}</p>
            <div style={styles.badges}>
              <span style={{
                ...styles.badge,
                backgroundColor: user.is_admin ? '#6f42c1' : '#2c5aa0',
              }}>
                {user.is_admin ? 'Admin' : 'User'}
              </span>
              {user.deleted_at ? (
                <span style={{...styles.badge, backgroundColor: '#dc3545'}}>Deleted</span>
              ) : user.is_active ? (
                <span style={{...styles.badge, backgroundColor: '#28a745'}}>Active</span>
              ) : (
                <span style={{...styles.badge, backgroundColor: '#ffc107', color: '#000'}}>Inactive</span>
              )}
            </div>
          </div>
        </div>

        <div style={styles.tabs}>
          <button
            style={{...styles.tab, ...(activeTab === 'profile' ? styles.tabActive : {})}}
            onClick={() => setActiveTab('profile')}
          >
            <i className="fas fa-user"></i> Profile
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'performance' ? styles.tabActive : {})}}
            onClick={() => setActiveTab('performance')}
          >
            <i className="fas fa-chart-line"></i> Performance
          </button>
        </div>

        {activeTab === 'profile' && (
          <div style={styles.tabContent}>
            <div style={styles.infoGrid}>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>User ID:</span>
                <span style={styles.infoValue}>{user.id}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Username:</span>
                <span style={styles.infoValue}>{user.username || 'N/A'}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Email:</span>
                <span style={styles.infoValue}>{user.email}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Role:</span>
                <span style={styles.infoValue}>{user.role}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Created:</span>
                <span style={styles.infoValue}>{formatDate(user.created_at)}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Last Active:</span>
                <span style={styles.infoValue}>{formatDate(user.last_active_at)}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Total Sessions:</span>
                <span style={styles.infoValue}>{user.total_sessions || 0}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Total Answers:</span>
                <span style={styles.infoValue}>{user.total_answers || 0}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Correct Answers:</span>
                <span style={styles.infoValue}>{user.correct_answers || 0}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Accuracy Rate:</span>
                <span style={styles.infoValue}>{user.accuracy_rate?.toFixed(1) || 0}%</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div style={styles.tabContent}>
            <h3 style={styles.sectionTitle}>Confidence Levels</h3>
            <div style={styles.confidenceGrid}>
              <div style={styles.confidenceCard}>
                <i className="fas fa-smile" style={{...styles.confidenceIcon, color: '#28a745'}}></i>
                <div style={styles.confidenceValue}>{user.high_confidence_answers || 0}</div>
                <div style={styles.confidenceLabel}>High Confidence</div>
              </div>
              <div style={styles.confidenceCard}>
                <i className="fas fa-meh" style={{...styles.confidenceIcon, color: '#ffc107'}}></i>
                <div style={styles.confidenceValue}>{user.medium_confidence_answers || 0}</div>
                <div style={styles.confidenceLabel}>Medium Confidence</div>
              </div>
              <div style={styles.confidenceCard}>
                <i className="fas fa-frown" style={{...styles.confidenceIcon, color: '#dc3545'}}></i>
                <div style={styles.confidenceValue}>{user.low_confidence_answers || 0}</div>
                <div style={styles.confidenceLabel}>Low Confidence</div>
              </div>
            </div>

            <h3 style={styles.sectionTitle}>Topic Performance</h3>
            {user.topic_performance && user.topic_performance.length > 0 ? (
              <div style={styles.topicsList}>
                {user.topic_performance.map((topic, index) => (
                  <div key={index} style={styles.topicCard}>
                    <div style={styles.topicName}>{topic.topic}</div>
                    <div style={styles.topicStats}>
                      <div style={styles.topicStat}>
                        <span style={styles.topicStatLabel}>Total:</span>
                        <span style={styles.topicStatValue}>{topic.total_answers}</span>
                      </div>
                      <div style={styles.topicStat}>
                        <span style={styles.topicStatLabel}>Correct:</span>
                        <span style={styles.topicStatValue}>{topic.correct_answers}</span>
                      </div>
                      <div style={styles.topicStat}>
                        <span style={styles.topicStatLabel}>Accuracy:</span>
                        <span style={{
                          ...styles.topicStatValue,
                          color: topic.accuracy_rate >= 80 ? '#28a745' : topic.accuracy_rate >= 60 ? '#ffc107' : '#dc3545'
                        }}>
                          {topic.accuracy_rate?.toFixed(1) || 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={styles.emptyText}>No topic performance data available</p>
            )}

            <h3 style={styles.sectionTitle}>Mastery Distribution</h3>
            {user.mastery_distribution && user.mastery_distribution.length > 0 ? (
              <div style={styles.masteryGrid}>
                {user.mastery_distribution.map((mastery, index) => (
                  <div key={index} style={styles.masteryCard}>
                    <div style={styles.masteryLevel}>{mastery.mastery_level}</div>
                    <div style={styles.masteryCount}>{mastery.count}</div>
                    <div style={styles.masteryPercentage}>{mastery.percentage?.toFixed(1) || 0}%</div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={styles.emptyText}>No mastery distribution data available</p>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: '20px',
    fontFamily: '"Open Sans", sans-serif',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
    gap: '20px',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #2c5aa0',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '18px',
    color: '#2c5aa0',
    fontWeight: '500',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
    gap: '20px',
  },
  errorIcon: {
    fontSize: '48px',
    color: '#dc3545',
  },
  errorText: {
    fontSize: '16px',
    color: '#666',
  },
  retryButton: {
    backgroundColor: '#2c5aa0',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  header: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '24px 30px',
    marginBottom: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: '1px solid #2c5aa0',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '16px',
    color: '#2c5aa0',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  editButton: {
    backgroundColor: '#2c5aa0',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  buttonIcon: {
    fontSize: '14px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    margin: '0 0 4px 0',
    color: '#2c5aa0',
  },
  subtitle: {
    fontSize: '14px',
    margin: '0',
    color: '#666666',
  },
  profileCard: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
    maxWidth: '1200px',
    margin: '0 auto',
    overflow: 'hidden',
  },
  profileHeader: {
    padding: '32px',
    display: 'flex',
    gap: '24px',
    alignItems: 'center',
    borderBottom: '1px solid #e0e0e0',
  },
  avatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#2c5aa0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  avatarIcon: {
    fontSize: '36px',
    color: 'white',
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#333333',
    margin: '0 0 8px 0',
  },
  profileEmail: {
    fontSize: '16px',
    color: '#666666',
    margin: '0 0 12px 0',
  },
  badges: {
    display: 'flex',
    gap: '8px',
  },
  badge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
    display: 'inline-block',
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #e0e0e0',
    padding: '0 32px',
  },
  tab: {
    backgroundColor: 'transparent',
    border: 'none',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: '500',
    color: '#666666',
    cursor: 'pointer',
    borderBottom: '3px solid transparent',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  tabActive: {
    color: '#2c5aa0',
    borderBottomColor: '#2c5aa0',
  },
  tabContent: {
    padding: '32px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  infoLabel: {
    fontSize: '12px',
    color: '#666666',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  infoValue: {
    fontSize: '16px',
    color: '#333333',
    fontWeight: '600',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333333',
    margin: '0 0 20px 0',
  },
  confidenceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '32px',
  },
  confidenceCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '24px',
    textAlign: 'center',
    border: '1px solid #e0e0e0',
  },
  confidenceIcon: {
    fontSize: '32px',
    marginBottom: '12px',
  },
  confidenceValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#333333',
    marginBottom: '8px',
  },
  confidenceLabel: {
    fontSize: '14px',
    color: '#666666',
    fontWeight: '500',
  },
  topicsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '32px',
  },
  topicCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #e0e0e0',
  },
  topicName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333333',
    marginBottom: '12px',
  },
  topicStats: {
    display: 'flex',
    gap: '24px',
  },
  topicStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  topicStatLabel: {
    fontSize: '14px',
    color: '#666666',
  },
  topicStatValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333333',
  },
  masteryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px',
  },
  masteryCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    border: '1px solid #e0e0e0',
  },
  masteryLevel: {
    fontSize: '14px',
    color: '#666666',
    fontWeight: '500',
    marginBottom: '8px',
    textTransform: 'capitalize',
  },
  masteryCount: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#333333',
    marginBottom: '4px',
  },
  masteryPercentage: {
    fontSize: '12px',
    color: '#666666',
  },
  emptyText: {
    fontSize: '14px',
    color: '#999999',
    textAlign: 'center',
    padding: '40px 20px',
  },
};

export default AdminUserDetail;
